import { createBrowserRouter, Navigate, type RouteObject, useLocation } from 'react-router';
import { LoginPage } from '../features/auth/LoginPage';
import { RequireAuth } from '../features/auth/RequireAuth';
import { oldPageRoutes, pageRoutes } from '../routes/page-routes';
import { AppLayout } from './layout/AppLayout';
import { NotFoundPage } from './NotFoundPage';

/** An old address: on to the page's address today with its query string, leaving the old one out of the history. */
function OldAddress({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

/**
 * The route tree. Addresses come from routes/page-routes.ts. Every page except the login is behind
 * RequireAuth; new pages are added under AppLayout (and to the sidebar in routes/nav-links.ts).
 * Each page is loaded when it is first opened (its own file in the build), so the first load stays small.
 */
export const routes: RouteObject[] = [
  { path: pageRoutes.login, element: <LoginPage /> },
  ...oldPageRoutes.map(({ from, to }) => ({ path: from, element: <OldAddress to={to} /> })),
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: pageRoutes.dashboard,
            lazy: async () => ({
              Component: (await import('../features/dashboard/DashboardPage')).DashboardPage,
            }),
          },
          {
            path: pageRoutes.imports,
            lazy: async () => ({ Component: (await import('../features/imports/ImportsPage')).ImportsPage }),
          },
          {
            path: pageRoutes.newImport,
            lazy: async () => ({
              Component: (await import('../features/imports/NewImportPage')).NewImportPage,
            }),
          },
          {
            path: pageRoutes.importDetail,
            lazy: async () => ({ Component: (await import('../features/imports/ImportPage')).ImportPage }),
          },
          {
            path: pageRoutes.products,
            lazy: async () => ({
              Component: (await import('../features/products/ProductsPage')).ProductsPage,
            }),
          },
          {
            path: pageRoutes.fetchRuns,
            lazy: async () => ({
              Component: (await import('../features/fetch/FetchRunsPage')).FetchRunsPage,
            }),
          },
          {
            path: pageRoutes.fetchRun,
            lazy: async () => ({ Component: (await import('../features/fetch/FetchRunPage')).FetchRunPage }),
          },
          {
            path: pageRoutes.proxies,
            lazy: async () => ({ Component: (await import('../features/fetch/ProxiesPage')).ProxiesPage }),
          },
          {
            path: pageRoutes.tester,
            lazy: async () => ({
              Component: (await import('../features/weight/ProductTesterPage')).ProductTesterPage,
            }),
          },
          {
            path: pageRoutes.weightTable,
            lazy: async () => ({
              Component: (await import('../features/weight/WeightTablePage')).WeightTablePage,
            }),
          },
          {
            path: pageRoutes.ubuyData,
            lazy: async () => ({
              Component: (await import('../features/ubuy-data/UbuyDataPage')).UbuyDataPage,
            }),
          },
          {
            path: pageRoutes.categories,
            lazy: async () => ({
              Component: (await import('../features/categories/CategoriesPage')).CategoriesPage,
            }),
          },
          {
            path: pageRoutes.publish,
            lazy: async () => ({ Component: (await import('../features/shopify/PublishPage')).PublishPage }),
          },
          {
            path: pageRoutes.review,
            lazy: async () => ({ Component: (await import('../features/review/ReviewPage')).ReviewPage }),
          },
          {
            path: pageRoutes.weightModel,
            lazy: async () => ({
              Component: (await import('../features/weight/WeightModelPage')).WeightModelPage,
            }),
          },
          {
            path: pageRoutes.storeRules,
            lazy: async () => ({
              Component: (await import('../features/weight/StoreRulesPage')).StoreRulesPage,
            }),
          },
          {
            path: pageRoutes.storeProductsTester,
            lazy: async () => ({
              Component: (await import('../features/store-check/StoreCheckPage')).StoreCheckPage,
            }),
          },
          {
            path: pageRoutes.imageComparison,
            lazy: async () => ({
              Component: (await import('../features/image-comparison/ImageComparisonPage'))
                .ImageComparisonPage,
            }),
          },
          {
            path: pageRoutes.howWeightsWork,
            lazy: async () => ({
              Component: (await import('../features/weight/HowWeightsWorkPage')).HowWeightsWorkPage,
            }),
          },
          {
            path: pageRoutes.aiUsage,
            lazy: async () => ({ Component: (await import('../features/ai/AiUsagePage')).AiUsagePage }),
          },
          {
            path: pageRoutes.jobs,
            lazy: async () => ({ Component: (await import('../features/jobs/JobsPage')).JobsPage }),
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter(routes);
